import { rem } from "polished";
import { ThemeKind } from "src/types";
import styled, { css } from "styled-components";

import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import { ColorLabels, getColor } from "@/components/editor/utils/use-color";
import noScrollBar from "@/styles/no-scrollbar";
import { UI_MODAL_BREAKPOINTS } from "@/utils/constants";

const baseStyles = css`
  font-family: ${({ theme }) =>
    theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
`;

export const Container = withViewportStyle("div")`
  max-height: 60vh;
  display: flex;
  flex-direction: column;

  @media (max-width: ${rem(UI_MODAL_BREAKPOINTS.LARGE)}) {
    max-height: unset;
    height: 100vh;
  }
  
  &.${ThemeKind.DEFAULT}, &.${ThemeKind.CROSBY}, &.${ThemeKind.HAZEL} {
    --start-color: ${({ theme }) =>
      getColor(theme.colors[ColorLabels.BACKGROUND]).alpha(0.4).css()};
    --end-color: ${({ theme }) =>
      getColor(theme.colors[ColorLabels.BACKGROUND]).alpha(1).css()};

    background-color: ${({ theme }) => theme.colors[ColorLabels.BACKGROUND]};
  }

  &.${ThemeKind.NOLAN} {
    --start-color: ${({ theme }) =>
      getColor(theme.colors[ColorLabels.VISUALS_2]).alpha(0.4).css()};
    --end-color: ${({ theme }) =>
      getColor(theme.colors[ColorLabels.VISUALS_2]).alpha(1).css()};
    
    background-color: ${({ theme }) => theme.colors[ColorLabels.VISUALS_2]};
  } 
`;

export const Header = withViewportStyle("div")<{ scrolled: boolean }>`
  box-shadow: ${(props) =>
    props.scrolled && "0px 4px 20px rgba(0, 0, 0, 0.16)"};

  padding: ${rem(48)} ${rem(48)} ${rem(16)};

  ${(props) => props.$viewport.mobile`
    padding: ${rem(64)} ${rem(16)} ${rem(16)};
  `}
`;

export const Thumbnail = withViewportStyle("img")`
  width: ${rem(124)};
  height: ${rem(124)};
  object-fit: cover;
  margin-right: ${rem(32)};

  &.${ThemeKind.CROSBY} {
    border-radius: ${rem(16)};
  }

  &.${ThemeKind.DEFAULT}, &.${ThemeKind.HAZEL} {
    border-radius: 100%;
  }

  ${(props) => props.$viewport.mobile`
    margin-right: ${rem(16)};
  `}
`;

export const Overview = withViewportStyle("div")`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding-top: ${rem(9)};

  ${(props) => props.$viewport.mobile`
    padding-top: ${rem(15)};
  `}
`;

export const NameField = withViewportStyle("h4")`
  ${baseStyles};
  font-size: ${rem(20)};
  line-height: ${rem(32)};
  margin-bottom: ${rem(8)};
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};

  &.${ThemeKind.CROSBY}, &.${ThemeKind.DEFAULT} {
    font-weight: 600;
  }

  &.${ThemeKind.HAZEL} {
    font-weight: 500;
  }

  &.${ThemeKind.NOLAN} {
    font-weight: 700;
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }

  ${(props) => props.$viewport.mobile`
    font-size: ${rem(18)};
    line-height: ${rem(26)};
  `}
`;

export const HeadlineField = withViewportStyle("p")`
  ${baseStyles};
  font-size: ${rem(14)};
  line-height: ${rem(26)};
  margin-bottom: ${rem(16)};
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};

  &.${ThemeKind.DEFAULT}, &.${ThemeKind.CROSBY} {
    font-weight: 400;
  }

  &.${ThemeKind.HAZEL} {
    font-weight: 300;
  }

  &.${ThemeKind.NOLAN} {
    font-weight: 400;
    line-height: ${rem(24)};
  }

  ${(props) => props.$viewport.mobile`
    font-size: ${rem(12)};

    &.${ThemeKind.DEFAULT},
    &.${ThemeKind.HAZEL},
    &.${ThemeKind.CROSBY} {
      font-weight: 300;
      line-height: ${rem(20)};
    }
  `}
`;

export const Footer = withViewportStyle("div")`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 ${rem(48)};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: ${rem(48)};
    background: linear-gradient(to bottom, var(--start-color) 0%, var(--end-color) 100%);
    pointer-events: none;
  }
  
  ${(props) => props.$viewport.mobile`
    padding: 0 ${rem(16)};
  `}
`;

export const ScrollWrapper = styled.div`
  width: 100%;
  ${noScrollBar};
`;

export const BioField = withViewportStyle("div")`
  ${baseStyles};
  font-weight: 300;
  font-size: ${rem(17)};
  line-height: ${rem(31)};
  color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  padding-bottom: ${rem(48)};

  &.${ThemeKind.NOLAN} {
    font-weight: 400;
    color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
  }

  ${(props) => props.$viewport.mobile`
    font-size: ${rem(14)};
    line-height: ${rem(26)};

    &.${ThemeKind.DEFAULT} {
      font-weight: 400;
    }
  `}
`;

export const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: ${rem(16)};

  svg {
    color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY_FONT]};
  }

  &.${ThemeKind.DEFAULT} {
    svg {
      color: ${({ theme }) => theme.colors[ColorLabels.PRIMARY]};
    }
  }

  &.${ThemeKind.NOLAN} {
    svg {
      color: ${({ theme }) => theme.colors[ColorLabels.SECONDARY_FONT]};
    }
  }
`;

export const ActionText = withViewportStyle("div")<{ actionable: boolean }>`
    *:first-child {
      ${(props) =>
        props.actionable &&
        `
        cursor: pointer;
      `}

      ${(props) =>
        props.actionable &&
        props.$viewport.mobile`
        text-decoration-line: underline;
      `}
    }
`;

export const ZoomCard = withViewportStyle("div")<{ actionable: boolean }>`
  overflow: hidden;
  // https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
  -webkit-transform: translate3d(0, 0, 0);

  &.${ThemeKind.DEFAULT} {
    border-radius: 50%;
    margin: ${rem(16)} auto ${rem(12)} auto
  }

  &.${ThemeKind.CROSBY} {
    border-radius: ${rem(16)};
  }

  &.${ThemeKind.HAZEL} {
    border-radius: 100%;
  }

  ${(props) =>
    props.actionable &&
    `
    cursor: pointer;
    
    img {
      transform: scale(1.0);
      transition: transform 0.2s ease-in-out;

      &:hover {
        transform: scale(1.1);
        transition: transform 0.2s ease-in-out;
      }
    }
  `}

  ${(props) => props.$viewport.mobile`
    img {
      transform: none !important;
    }
  `}
`;
